<template>
  <transition name="mobile-install-banner-slide">
    <div
      v-if="visible"
      class="mobile-install-banner"
      role="region"
      aria-label="Install Bzioup"
    >
      <div class="mobile-install-banner__content">
        <span class="mobile-install-banner__heart"
aria-hidden="true">♥</span>
        <div class="mobile-install-banner__text">
          <span class="mobile-install-banner__title">Bzioup</span>
          <span class="mobile-install-banner__subtitle">
            {{ subtitleText }}
          </span>
        </div>
      </div>
      <a
        :href="storeUrl"
        class="mobile-install-banner__cta"
        rel="noopener"
        @click="onCtaClick"
      >
        Get
      </a>
      <button
        class="mobile-install-banner__dismiss"
        aria-label="Dismiss"
        @click="dismiss"
      >
        ×
      </button>
    </div>
  </transition>
</template>

<script>
/**
 * Mobile-only sticky bottom banner nudging the visitor to install
 * the app. Rendered globally from hello/src/App.vue.
 *
 * Conditions to show:
 *   - User Agent matches iOS or Android (desktop never sees it).
 *   - User hasn't dismissed it in this browser (localStorage flag).
 *
 * Tradeoffs:
 *   - We can't reliably detect "app already installed" from a web
 *     page (browsers don't expose this). So users who already have
 *     the app may see the banner. they can dismiss once and it's
 *     gone forever on that browser.
 *   - The banner targets the user's specific OS (iOS → App Store,
 *     Android → Play Store). clearer than offering both.
 *
 * Persistence key uses a versioned suffix so we can re-prompt all
 * users once apps actually exist (just bump the version).
 */
const DISMISS_KEY = 'bzioup-mobile-install-banner-dismissed-v1'

export default {
  name: 'MobileInstallBanner',
  data() {
    return {
      platform: 'desktop', // 'ios' | 'android' | 'desktop'
      dismissed: false,
    }
  },
  computed: {
    visible() {
      return !this.dismissed && (this.platform === 'ios' || this.platform === 'android')
    },
    storeUrl() {
      if (this.platform === 'ios') {
        return 'https://apps.apple.com/app/bzioup/id6758737021'
      }
      if (this.platform === 'android') {
        return 'https://play.google.com/store/apps/details?id=com.bzioup.app'
      }
      return '#'
    },
    subtitleText() {
      if (this.platform === 'ios') return 'Get it on the App Store'
      if (this.platform === 'android') return 'Get it on Google Play'
      return ''
    },
  },
  mounted() {
    // SSR-safety: only touch window/navigator in mounted (this site
    // doesn't seem to use SSR but the guard is cheap and future-proof).
    if (typeof window === 'undefined') return
    this.detectPlatform()
    this.dismissed = window.localStorage.getItem(DISMISS_KEY) === '1'
  },
  methods: {
    detectPlatform() {
      const ua = (navigator.userAgent || '').toLowerCase()
      // iPadOS 13+ reports as Mac; check for touch as a tiebreaker.
      // We'd rather over-show on iPad than under-show. the worst
      // case is "dismiss once and move on."
      const isIpadOS =
        /macintosh/.test(ua) && navigator.maxTouchPoints > 1
      if (/iphone|ipad|ipod/.test(ua) || isIpadOS) {
        this.platform = 'ios'
      } else if (/android/.test(ua)) {
        this.platform = 'android'
      } else {
        this.platform = 'desktop'
      }
    },
    dismiss() {
      this.dismissed = true
      try {
        window.localStorage.setItem(DISMISS_KEY, '1')
      } catch (err) {
        // Private mode etc.. banner just won't be remembered
        // across navigations. Not worth surfacing to the user.
      }
    },
    onCtaClick() {
      // Tapping "Get" implies acknowledged → also dismisses so the
      // banner doesn't reappear on every page load after they've
      // gone to the store. Doesn't block the navigation (href fires
      // first, this runs concurrently).
      this.dismiss()
    },
  },
}
</script>

<style lang="scss" scoped>
.mobile-install-banner {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 8px 24px rgba(8, 20, 72, 0.18);
  color: var(--bzioup-text-color, #222);
  font-size: 13px;
}

.mobile-install-banner__content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.mobile-install-banner__heart {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--bzioup-color-pink);
  color: #fff;
  font-size: 16px;
  flex-shrink: 0;
}

.mobile-install-banner__text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  min-width: 0;
}

.mobile-install-banner__title {
  font-weight: 600;
  font-size: 14px;
}

.mobile-install-banner__subtitle {
  font-size: 12px;
  color: var(--bzioup-text-color-soft, #777);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-install-banner__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  background: var(--bzioup-color-pink);
  color: #fff;
  text-decoration: none;
  border-radius: 999px;
  font-weight: 600;
  font-size: 13px;
  flex-shrink: 0;
  transition: opacity 150ms ease;

  &:hover { opacity: 0.9; }
}

.mobile-install-banner__dismiss {
  background: transparent;
  border: none;
  color: var(--bzioup-text-color-soft, #888);
  font-size: 22px;
  line-height: 1;
  padding: 0 4px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover { color: var(--bzioup-text-color, #222); }
}

/* Slide-up entrance + slide-down exit. Quick (220ms) so it doesn't
   compete for attention on first paint. */
.mobile-install-banner-slide-enter-active,
.mobile-install-banner-slide-leave-active {
  transition: transform 220ms ease, opacity 220ms ease;
}
.mobile-install-banner-slide-enter-from,
.mobile-install-banner-slide-leave-to {
  transform: translateY(calc(100% + 24px));
  opacity: 0;
}
</style>
